import { useState } from "react";
import { Box, Card, Typography, IconButton, Collapse, useTheme, useMediaQuery } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const CollapsibleSection = ({
    title,
    children,
    defaultExpanded = true,
    showCard = false,
    cardSx = {},
    headerSx = {},
    ...props
}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // On desktop, just render children without collapsible wrapper
    if (isDesktop) {
        if (showCard) {
            return (
                <Card
                    sx={{
                        alignSelf: "stretch",
                        padding: { xs: 1.5, sm: 2 },
                        background: "white",
                        borderRadius: 2,
                        outline: `1px ${delftBlue[100]} solid`,
                        outlineOffset: "-1px",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        overflow: "visible",
                        ...cardSx,
                    }}
                    {...props}
                >
                    {children}
                </Card>
            );
        }

        return (
            <Box
                sx={{
                    alignSelf: "stretch",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    overflow: "visible",
                    ...cardSx,
                }}
                {...props}
            >
                {children}
            </Box>
        );
    }

    // On mobile/tablet, show full collapsible section
    const content = (
        <>
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    paddingBottom: isExpanded ? { xs: 1, sm: 1.5 } : 0,
                    cursor: "pointer",
                    userSelect: "none",
                    touchAction: "manipulation",
                    "&:active": {
                        opacity: 0.7,
                    },
                    ...headerSx,
                }}
                onClick={toggleExpand}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpand();
                    }
                }}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${title}`}
            >
                <Typography
                    sx={{
                        color: delftBlue[500],
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontFamily: "Audiowide, sans-serif",
                        fontWeight: 400,
                        lineHeight: 1.2,
                        flex: 1,
                    }}
                >
                    {title}
                </Typography>
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand();
                    }}
                    sx={{
                        padding: { xs: 0.75, sm: 1 },
                        color: delftBlue[500],
                        minWidth: { xs: 40, sm: 44 },
                        minHeight: { xs: 40, sm: 44 },
                        touchAction: "manipulation",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                        "&:active": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                    }}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse
                in={isExpanded}
                timeout={{ enter: 300, exit: 200 }}
                unmountOnExit={false}
                sx={{
                    width: "100%",
                    "& .MuiCollapse-wrapper": {
                        width: "100%",
                    },
                    "& .MuiCollapse-wrapperInner": {
                        width: "100%",
                    },
                }}
            >
                <Box
                    sx={{
                        paddingTop: { xs: 1, sm: 1.5 },
                        width: "100%",
                        display: "block",
                        overflow: "visible",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </>
    );

    if (showCard) {
        return (
            <Card
                sx={{
                    alignSelf: "stretch",
                    padding: { xs: 1.5, sm: 2 },
                    background: "white",
                    borderRadius: 2,
                    outline: `1px ${delftBlue[100]} solid`,
                    outlineOffset: "-1px",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    minHeight: isExpanded ? "auto" : "auto",
                    overflow: "visible",
                    ...cardSx,
                }}
                {...props}
            >
                {content}
            </Card>
        );
    }

    return (
        <Box
            sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                overflow: "visible",
                ...cardSx,
            }}
            {...props}
        >
            {content}
        </Box>
    );
};

export default CollapsibleSection;

